// src/components/InternalTestExperiencesPage.js

import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { motion } from 'framer-motion';

import './InternalTestExperiencesPage.css';

const InternalTestExperiencesPage = () => {
    const location = useLocation();
    const isEnglish = location.pathname.startsWith('/en');

    const form = useRef();
    const recaptchaRef = useRef();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const [selectedExp, setSelectedExp] = useState('tea');
    const [isYuzenModalOpen, setIsYuzenModalOpen] = useState(false);
    const [isShodoModalOpen, setIsShodoModalOpen] = useState(false);
    const [isKinpakuModalOpen, setIsKinpakuModalOpen] = useState(false);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sendEmail = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const token = recaptchaRef.current.getValue();

        if (!token) {
            setSubmitMessage(isEnglish ? "Please complete the reCAPTCHA." : "reCAPTCHA認証を完了してください。");
            setIsSubmitting(false);
            return;
        }

        const expName = form.current.experience.options[form.current.experience.selectedIndex].text;
        const yuzenPlan = (selectedExp === 'yuzen' || selectedExp === 'multiple') ? form.current.guide_plan.value : 'N/A';

        // ルールに則り、全データを1つの文字列に結合
        const combinedMessage = `
〓〓〓 特別体験のご予約 〓〓〓

【ご希望の体験】 ${expName}
【ご希望日時】 ${form.current.desired_date.value}
【参加人数】 ${form.current.participants.value}名
${(selectedExp === 'yuzen' || selectedExp === 'multiple') ? `【加賀友禅プラン】 ${yuzenPlan}\n` : ''}
【メッセージ】
${form.current.message.value}
        `.trim();

        const autoSubject = isEnglish
            ? `Reservation Request: ${expName}`
            : `【特別体験予約】${expName}`;

        const templateParams = {
            user_name: form.current.user_name.value,
            user_email: form.current.user_email.value,
            subject: autoSubject,
            message: combinedMessage,
            'g-recaptcha-response': token,
        };

        // プロジェクトルールの専用IDを強制適用
        emailjs.send(
            'service_nsfmgss',
            'template_mxh45o8',
            templateParams,
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        )
            .then(() => {
                setSubmitMessage(isEnglish ? 'Sent successfully.' : 'お申し込みが完了しました。');
                form.current.reset();
                recaptchaRef.current.reset();
            }, (error) => {
                console.error('EmailJS Error:', error);
                setSubmitMessage(isEnglish ? 'Failed to send.' : '送信に失敗しました。');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const fadeInProps = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const content = isEnglish ? {
        pageTitle: 'Exclusive Cultural Experiences',
        pageDesc: 'Elevate your stay at Kanazawa Stay Akebono with our handpicked VIP cultural programs. Immerse yourself in the profound traditions of Japan through these private, unforgettable experiences.',

        tea: {
            title: 'Private Tea Ceremony',
            desc: 'Step into a world of tranquility within a historic machiya. Tea master Yuta Takahashi invites you to a private session where you will experience the profound art of the Japanese tea ceremony.',
            instructorName: 'Tea Master: Yuta Takahashi',
            price: '1-2 people: 13,200 JPY / +3,300 JPY per additional guest',
            duration: 'Approx. 1 hour',
            location: 'Kanazawa Stay Akebono (In-house)',
            access: 'Conducted inside your villa',
            linkText: 'View on Instagram ↗',
            linkUrl: 'https://www.instagram.com/tea.base_hakutaku/',
        },

        yuzen: {
            title: 'Kaga Yuzen Silk Dyeing',
            desc: 'Step into the private studio of "Maida Sengakougei", a master of Kanazawa\'s traditional Kaga Yuzen dyeing, located just a 1-minute walk from our machiya. Enjoy an exclusive VIP program.',
            instructorName: 'Studio: Maida Sengakougei',
            price: 'With English Guide: 110,000 JPY / No Guide: 88,000 JPY (Up to 6 guests)',
            duration: 'Approx. 90 min',
            location: 'Maida Sengakougei Studio',
            access: 'Approx. 1 min walk from Akebono',
            linkText: 'Official Website ↗',
            linkUrl: 'http://www.maida-yuzen.com/',
            btnItinerary: 'Production Process Details',
            itineraryTitle: 'The 13 Steps of Kaga Yuzen Production',
            itinerarySteps: [
                { time: 'Step 1', title: 'Pattern Design', desc: 'Creating a full-size design based on the beauty of nature.' },
                { time: 'Step 2', title: 'Temporary Tailoring', desc: 'Sewing white fabric into the shape of a kimono.' },
                { time: 'Step 3', title: 'Transferring Design', desc: 'Tracing the pattern using "Aobana" liquid, which disappears in water.' },
                { time: 'Step 4', title: 'Applying Paste', desc: 'Drawing "Itomenori" outlines using rice or rubber paste to prevent dyes from spreading.' },
                { time: 'Step 5', title: 'Applying Broth', desc: 'Applying soybean broth to the back of the fabric to set the paste.' },
                { time: 'Step 6', title: 'Coloring', desc: 'The heart of production; applying dyes inside the outlines with brushes.' },
                { time: 'Step 7', title: 'Steaming (Fixation)', desc: 'Fixing the colors by applying heat.' },
                { time: 'Step 8', title: 'Protecting Design', desc: 'Covering colored parts with paste to protect them during background dyeing.' },
                { time: 'Step 9', title: 'Dyeing Background', desc: 'Dyeing the entire background of the fabric evenly with a brush.' },
                { time: 'Step 10', title: 'Fabric Steaming', desc: 'Placing the fabric in a steam-filled box for about an hour to permanently set all dyes.' },
                { time: 'Step 11', title: 'Washing (Mizumoto)', desc: 'Washing away excess dye and paste in running water (Yuzen Nagashi).' },
                { time: 'Step 12', title: 'Finishing (Yunoshi)', desc: 'Adjusting fabric weave and width using steam.' },
                { time: 'Step 13', title: 'Final Tailoring', desc: 'Sewing the finished fabric into its final kimono form.' }
            ],
            itineraryNote: '※ Our program allows you to see these authentic processes at the working studio. All steps are handled consistently in-house, but the specific processes available for viewing depend on the day\'s production schedule.'
        },

        shodo: {
            title: 'Zen Calligraphy Experience',
            desc: 'At "Culture Lab Nomachi," immerse yourself in a quiet moment of self-reflection through Shodo (calligraphy). Express meaningful characters with a brush and take home your creation as a hanging scroll or a folding fan.',
            displayFeature: 'Special: You can hang your finished scroll in the alcove of Akebono during your stay to complete your traditional experience.',
            instructorName: 'Instructor: Culture Lab Nomachi',
            price: '10,000 JPY per person',
            timeSlots: '10:00 / 13:00 / 14:30 / 17:30',
            regularHoliday: 'Sundays',
            capacity: 'Up to 6 guests',
            location: 'Culture Lab Nomachi (On-site)',
            access: 'Approx. 20 min walk from Akebono',
            duration: 'Approx. 1 hour',
            linkText: 'Google Maps ↗',
            linkUrl: 'https://maps.app.goo.gl/ACjBWESRygJpaiWG7',
            veganNote: 'Note: Brushes made from animal hair are used. If you are vegan, please contact us in advance.',
            btnItinerary: 'View Program Details',
            itineraryTitle: 'Calligraphy Experience Program (60 min)',
            itinerarySteps: [
                { time: 'Step 1', title: 'Background', desc: 'Understanding the spiritual background and history of Japanese calligraphy.' },
                { time: 'Step 2', title: 'The Basics', desc: 'Mastering the correct brush grip, posture, and mindful breathing.' },
                { time: 'Step 3', title: 'Practice & Creation', desc: 'Selecting a meaningful character and expressing it on a scroll or fan.' },
                { time: 'Step 4', title: 'Conclusion', desc: 'Sharing the meaning of your work and concluding the session.' }
            ],
            itineraryNote: '※ Brushes made from animal hair are used. Please inform us if you have any preferences.'
        },

        kinpaku: {
            title: 'Kanazawa Gold Leaf Experience',
            desc: 'Experience the brilliance of Kanazawa with "Imai Kinpaku," a master of gold leaf since 1898. We offer Akebono guests an exclusive, fully packaged premium experience.',
            displayFeature: 'Akebono Exclusive: Includes a special 10% discount on shopping at the store on the day of your visit.',
            instructorName: 'Imai Kinpaku Main Store',
            price: '7,500 JPY per person (Special Package)',
            location: 'Imai Kinpaku Main Store (7-3 Saiwaimachi, Kanazawa)',
            access: 'Approx. 3 min walk from Akebono',
            duration: 'Approx. 45 min total',
            linkText: 'Google Maps ↗',
            linkUrl: 'https://maps.app.goo.gl/QG5NemMxj1NfoHeD7',
            btnItinerary: 'View Package Contents',
            itineraryTitle: 'Gold Leaf Experience Package Contents',
            itinerarySteps: [
                { time: 'Experience 1', title: 'Gold Leaf Tumbler Making', desc: 'Create your own original tumbler by applying delicate gold leaf (Approx. 30 min).' },
                { time: 'Experience 2', title: 'Gold Leaf Soft Serve', desc: 'Experience applying gold leaf to a soft-serve ice cream before enjoying it (Approx. 15 min).' },
                { time: 'Experience 3', title: 'Gold Leaf Coffee', desc: 'Takeout coffee topped with luxurious gold leaf.' },
                { time: 'Special Bonus', title: 'Shopping Discount', desc: 'Enjoy a 10% discount on all purchases made at the store on the day of the experience.' }
            ],
            itineraryNote: '※ All of the above are included in this one exclusive package.'
        },

        formTitle: 'Reservation Form',
        formExpLabel: 'Select Experience',
        formExpOptionTea: 'Private Tea Ceremony',
        formExpOptionYuzen: 'Kaga Yuzen Silk Dyeing',
        formExpOptionShodo: 'Zen Calligraphy',
        formExpOptionKinpaku: 'Gold Leaf Experience',
        formExpOptionMultiple: 'Multiple Experiences',
        formName: 'Name',
        formEmail: 'Email',
        formDate: 'Desired Date & Time',
        formParticipants: 'Participants',
        formPlanLabel: 'Kaga Yuzen Guide Option',
        formPlanOption1: 'With Private English Guide (110,000 JPY)',
        formPlanOption2: 'Without Guide / Japanese Only (88,000 JPY)',
        formMessage: 'Message',
        submitButton: 'Apply Now'
    } : {
        pageTitle: '特別文化体験',
        pageDesc: '金沢之旅宿 あけぼのでの滞在をさらに特別なものにする、最高峰のプライベート文化体験をご用意いたしました。金沢が世界に誇る伝統と美に、深く触れるひとときをお過ごしください。',

        tea: {
            title: '一期一会の茶会体験',
            desc: '歴史の息づかいを感じる町家空間へ、金沢で人気の茶道家・髙橋勇太氏をお招きします。あなたのためだけに設えられたプライベートな空間で、ゆったりと流れる時に身を委ねてみませんか。',
            instructorName: '亭主：髙橋 勇太',
            price: '1〜2名様 13,200円 / 3名様以上 1名様につき+3,300円',
            duration: '約1時間',
            location: '金沢之旅宿 あけぼの（宿内）',
            access: '宿内にて実施',
            linkText: 'Instagramを見る ↗',
            linkUrl: 'https://www.instagram.com/tea.base_hakutaku/',
        },

        yuzen: {
            title: '加賀友禅「知る・創る・着る」工房体験',
            desc: '宿から徒歩1分の現役作家「毎田染画工芸」の工房を貸し切りに。歴史解説から彩色体験、そして加賀友禅の着付けまで、金沢の伝統美にどっぷりと浸る特別な90分をお届けします。',
            instructorName: '工房：毎田染画工芸',
            price: '英語専属通訳あり: 110,000円 / 通訳なし: 88,000円（1グループ6名様まで）',
            duration: '約90分',
            location: '毎田染画工芸',
            access: '宿から徒歩約1分',
            linkText: '公式サイトを見る ↗',
            linkUrl: 'http://www.maida-yuzen.com/',
            btnItinerary: '伝統の制作工程・詳細',
            itineraryTitle: '加賀友禅ができるまで（全13工程）',
            itinerarySteps: [
                { time: '工程 1', title: '図案作成', desc: '自然の美しさを文様化し、原寸大のデザインを紙に描きます。' },
                { time: '工程 2', title: '仮仕立て', desc: '白生地を一度着物の形に仕立て、寸法を確認します。' },
                { time: '工程 3', title: '下絵', desc: '水で消える「青花」の汁を使い、図案を白生地に写し取ります。' },
                { time: '工程 4', title: '糊置き', desc: '米糊やゴム糊を用いた「糸目糊」で、彩色した色がにじみ出さないよう輪郭を描きます。' },
                { time: '工程 5', title: '地入れ', desc: '大豆の汁（豆汁）を引き、糊の食い込みと染料の定着を助けます。' },
                { time: '工程 6', title: '彩色', desc: '加賀友禅の心臓部。筆を使い、輪郭の内側に色をさしていきます。' },
                { time: '工程 7', title: '下蒸し', desc: '熱をかけることで、彩色した色を生地に定着させます。' },
                { time: '工程 8', title: '中埋め（糊伏せ）', desc: '彩色部分を糊で覆い、地色を染める際に色が混ざるのを防ぎます。' },
                { time: '工程 9', title: '地染め（引き染）', desc: '刷毛を使い、ムラなく生地全体を染め上げます。' },
                { time: '工程 10', title: '本蒸し', desc: '蒸気の箱に入れ約一時間蒸すことで、全ての染色を完全に定着させます。' },
                { time: '工程 11', title: '水洗（水元）', desc: '余分な染料や糊を流水で洗い流します（友禅流し）。' },
                { time: '工程 12', title: '湯のし', desc: '蒸気で生地の目や幅を整え、絹本来の風合いに戻します。' },
                { time: '工程 13', title: '仕立て', desc: '最終的な着物の形に縫い上げ、完成となります。' }
            ],
            itineraryNote: '※体験プログラムでは、これらの本物の制作現場を間近で見学・体験いただけます。全工程を一貫して行っていますが、見学できる工程は当日の制作状況によって異なります。'
        },

        shodo: {
            title: '自分と向き合う書道体験',
            desc: '金沢の町家「カルチャーラボ野町」にて、書道を通じて自分自身と向き合う静かな時間をお届けします。意味を持った文字を筆で表現し、最後は掛け軸や扇子として形に残る思い出をお持ち帰りいただけます。',
            displayFeature: '特典：完成した掛け軸は、滞在中に「あけぼの」の床の間へ掛けてお楽しみいただくことも可能です。自らの書と共に過ごす、特別な町家の夜をご堪能ください。',
            instructorName: '講師：カルチャーラボ野町',
            price: '1名様 10,000円（税込）',
            timeSlots: '10:00〜 / 13:00〜 / 14:30〜 / 17:30〜',
            regularHoliday: '日曜日',
            capacity: '6名様まで',
            location: '現地実施（カルチャーラボ野町）',
            access: '宿から徒歩約20分',
            duration: '約1時間',
            linkText: 'Google Mapsで場所を確認 ↗',
            linkUrl: 'https://maps.app.goo.gl/ACjBWESRygJpaiWG7',
            veganNote: '※動物性の筆を使用しています。ヴィーガンの方は事前にご確認ください。',
            btnItinerary: '体験プログラムの行程',
            itineraryTitle: '書道体験プログラムの行程（60分）',
            itinerarySteps: [
                { time: 'Step 1', title: '導入', desc: '書道が持つ精神性や、文字の背景にある思想について学びます。' },
                { time: 'Step 2', title: '基本', desc: '正しい筆の持ち方、姿勢、そして心を整える呼吸法を習得します。' },
                { time: 'Step 3', title: '実践', desc: '自分にふさわしい文字を選定し、掛け軸または扇子に表現します。' },
                { time: 'Step 4', title: '仕上げ', desc: '完成した作品の意味を共有し、体験を締めくくります。' }
            ],
            itineraryNote: '※本プログラムでは伝統的な動物性の筆を使用しております。'
        },

        kinpaku: {
            title: '金箔が彩る伝統体験',
            desc: '金沢の象徴である「金箔」。明治31年創業の老舗「今井金箔」との特別コラボレーションにより、制作から味わうことまで、金沢の美を余すことなく体験できる「あけぼの限定プレミアムパッケージ」をご用意いたしました。',
            displayFeature: 'あけぼの限定特典：体験ご参加の方は、当日の店舗内でのお買い物が10%OFFとなります。',
            instructorName: '今井金箔 本店',
            price: '1名様 7,500円（税込）',
            location: '今井金箔 本店（金沢市幸町7-3）',
            access: '宿から徒歩約3分',
            duration: '約45分',
            linkText: 'Google Mapsで場所を確認 ↗',
            linkUrl: 'https://maps.app.goo.gl/QG5NemMxj1NfoHeD7',
            btnItinerary: 'パッケージ内容を見る',
            itineraryTitle: '金箔プレミアムパッケージ内容',
            itinerarySteps: [
                { time: '体験 1', title: '金箔タンブラー制作体験', desc: 'タンブラーに金箔を貼り、世界に一つだけのオリジナル作品を制作します（所要時間：約30分）。' },
                { time: '体験 2', title: '金箔ソフト体験', desc: 'ソフトクリームに金箔を自ら貼る体験をお楽しみいただけます（所要時間：約15分）。' },
                { time: '体験 3', title: '金箔コーヒー提供', desc: '金箔入りのコーヒーをテイクアウト形式でご提供いたします。' },
                { time: '特典', title: 'お買い物10%OFF', desc: '体験参加者限定で、当日のお買い物が10%OFFとなります。' }
            ],
            itineraryNote: '※上記すべてがセットになった、あけぼの限定の豪華な体験プランです。'
        },

        formTitle: '特別体験 お申し込みフォーム',
        formExpLabel: 'ご希望の体験',
        formExpOptionTea: 'お茶会体験',
        formExpOptionYuzen: '加賀友禅工房体験',
        formExpOptionShodo: '書道体験',
        formExpOptionKinpaku: '金箔体験',
        formExpOptionMultiple: '複数の体験を希望',
        formName: 'お名前',
        formEmail: 'メールアドレス',
        formDate: 'ご希望日時',
        formParticipants: 'ご参加人数',
        formPlanLabel: '加賀友禅 ご希望プラン',
        formPlanOption1: '英語専属通訳ありプラン (110,000円)',
        formPlanOption2: '通訳なしプラン (88,000円)',
        formMessage: 'メッセージ (任意)',
        submitButton: '申し込む'
    };

    return (
        <>
            <Helmet>
                <title>{isEnglish ? '【Internal】Exclusive Experiences | Akebono' : '【社内確認用】特別体験｜あけぼの'}</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="akebono-sp-page">
                {/* ====== ミニマルヘッダー ====== */}
                <div className="akebono-sp-header">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0 }}
                        className="akebono-sp-main-title"
                    >
                        {content.pageTitle}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.0, delay: 0.3 }}
                        className="akebono-sp-sub-title"
                    >
                        {isEnglish ? 'Exclusive Programs' : 'あけぼの限定プログラム'}
                    </motion.p>
                </div>

                {/* クイックナビゲーション (4項目) */}
                <div className="akebono-sp-nav-container">
                    <div className="akebono-sp-nav-card" onClick={() => scrollToSection('tea-section')}>
                        <div className="akebono-sp-nav-bg" style={{ backgroundImage: "url('/images/tea-1.jpg')" }}></div>
                        <div className="akebono-sp-nav-overlay">
                            <h3>{isEnglish ? '01. Tea Ceremony' : '01. お茶会体験'}</h3>
                            <span className="akebono-sp-nav-btn">{isEnglish ? 'Details ↓' : '詳細 ↓'}</span>
                        </div>
                    </div>
                    <div className="akebono-sp-nav-card" onClick={() => scrollToSection('yuzen-section')}>
                        <div className="akebono-sp-nav-bg" style={{ backgroundImage: "url('/images/maida-studio.jpg')" }}></div>
                        <div className="akebono-sp-nav-overlay">
                            <h3>{isEnglish ? '02. Kaga Yuzen' : '02. 加賀友禅体験'}</h3>
                            <span className="akebono-sp-nav-btn">{isEnglish ? 'Details ↓' : '詳細 ↓'}</span>
                        </div>
                    </div>
                    <div className="akebono-sp-nav-card" onClick={() => scrollToSection('shodo-section')}>
                        <div className="akebono-sp-nav-bg" style={{ backgroundImage: "url('/images/shodo-bg.jpg')" }}></div>
                        <div className="akebono-sp-nav-overlay">
                            <h3>{isEnglish ? '03. Calligraphy' : '03. 書道体験'}</h3>
                            <span className="akebono-sp-nav-btn">{isEnglish ? 'Details ↓' : '詳細 ↓'}</span>
                        </div>
                    </div>
                    <div className="akebono-sp-nav-card" onClick={() => scrollToSection('kinpaku-section')}>
                        <div className="akebono-sp-nav-bg" style={{ backgroundImage: "url('/images/kinpaku-main.jpg')" }}></div>
                        <div className="akebono-sp-nav-overlay">
                            <h3>{isEnglish ? '04. Gold Leaf' : '04. 金箔体験'}</h3>
                            <span className="akebono-sp-nav-btn">{isEnglish ? 'Details ↓' : '詳細 ↓'}</span>
                        </div>
                    </div>
                </div>

                <div className="akebono-sp-container">
                    <motion.p className="akebono-sp-description" {...fadeInProps}>
                        {content.pageDesc}
                    </motion.p>

                    {/* ====== 1. お茶体験 (1枚画像) ====== */}
                    <motion.div id="tea-section" className="akebono-sp-section scroll-target" {...fadeInProps}>
                        <h2 className="akebono-sp-title">
                            <span className="akebono-sp-num">01</span>
                            {content.tea.title}
                        </h2>
                        <div className="akebono-sp-layout">
                            <div className="akebono-sp-img-wrapper" style={{ maxHeight: '450px', alignSelf: 'center' }}>
                                <img src="/images/tea-1.jpg" alt="Tea Master" />
                            </div>
                            <div className="akebono-sp-content-box">
                                <h3 className="akebono-sp-instructor-name">{content.tea.instructorName}</h3>
                                <p style={{ marginBottom: '20px' }}>{content.tea.desc}</p>
                                <ul className="akebono-sp-info-list">
                                    <li><strong>Price:</strong> {content.tea.price}</li>
                                    <li><strong>Duration:</strong> {content.tea.duration}</li>
                                    <li><strong>Location:</strong> {content.tea.location}</li>
                                    <li><strong>Access:</strong> {content.tea.access}</li>
                                </ul>
                                <a href={content.tea.linkUrl} target="_blank" rel="noopener noreferrer" className="akebono-sp-link">
                                    {content.tea.linkText}
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* ====== 2. 加賀友禅体験 (1枚画像) ====== */}
                    <motion.div id="yuzen-section" className="akebono-sp-section scroll-target" {...fadeInProps}>
                        <h2 className="akebono-sp-title">
                            <span className="akebono-sp-num">02</span>
                            {content.yuzen.title}
                        </h2>
                        <div className="akebono-sp-layout">
                            <div className="akebono-sp-content-box">
                                <h3 className="akebono-sp-instructor-name">{content.yuzen.instructorName}</h3>
                                <p style={{ marginBottom: '20px' }}>{content.yuzen.desc}</p>
                                <ul className="akebono-sp-info-list">
                                    <li><strong>Price:</strong> {content.yuzen.price}</li>
                                    <li><strong>Duration:</strong> {content.yuzen.duration}</li>
                                    <li><strong>Location:</strong> {content.yuzen.location}</li>
                                    <li><strong>Access:</strong> {content.yuzen.access}</li>
                                </ul>
                                <button onClick={() => setIsYuzenModalOpen(true)} className="akebono-sp-btn-gold">
                                    {content.yuzen.btnItinerary}
                                </button>
                                <br />
                                <a href={content.yuzen.linkUrl} target="_blank" rel="noopener noreferrer" className="akebono-sp-link">
                                    {content.yuzen.linkText}
                                </a>
                            </div>
                            <div className="akebono-sp-img-wrapper" style={{ maxHeight: '450px', alignSelf: 'center' }}>
                                <img src="/images/maida-studio.jpg" alt="Kaga Yuzen Studio" />
                            </div>
                        </div>
                    </motion.div>

                    {/* ====== 3. 書道体験 (3枚コラージュ/Sticky) ====== */}
                    <motion.div id="shodo-section" className="akebono-sp-section scroll-target" {...fadeInProps}>
                        <h2 className="akebono-sp-title">
                            <span className="akebono-sp-num">03</span>
                            {content.shodo.title}
                        </h2>
                        <div className="akebono-sp-layout akebono-sp-sticky-layout">
                            <div className="akebono-sp-content-box akebono-sp-sticky-content">
                                <h3 className="akebono-sp-instructor-name">{content.shodo.instructorName}</h3>
                                <p style={{ marginBottom: '15px' }}>{content.shodo.desc}</p>
                                <div className="akebono-sp-feature-box">
                                    <p><strong>{isEnglish ? 'Exclusive Perk:' : 'あけぼの限定特典：'}</strong></p>
                                    <p>{content.shodo.displayFeature}</p>
                                    <div className="akebono-sp-display-img">
                                        <img src="/images/shodo-display.jpg" alt="Calligraphy" className="akebono-sp-inline-img" />
                                    </div>
                                </div>
                                <ul className="akebono-sp-info-list">
                                    <li><strong>Price:</strong> {content.shodo.price}</li>
                                    <li><strong>Time:</strong> {content.shodo.timeSlots}</li>
                                    <li><strong>Location:</strong> {content.shodo.location}</li>
                                    <li><strong>Access:</strong> {content.shodo.access}</li>
                                    <li className="akebono-sp-vegan">{content.shodo.veganNote}</li>
                                </ul>
                                <button onClick={() => setIsShodoModalOpen(true)} className="akebono-sp-btn-gold">
                                    {content.shodo.btnItinerary}
                                </button>
                                <br />
                                <a href={content.shodo.linkUrl} target="_blank" rel="noopener noreferrer" className="akebono-sp-link">
                                    {content.shodo.linkText}
                                </a>
                            </div>
                            <div className="akebono-sp-collage-wrapper">
                                <img src="/images/shodo-1.jpg" alt="Calligraphy 1" className="akebono-sp-collage-img akebono-sp-img-left" />
                                <img src="/images/shodo-2.jpg" alt="Calligraphy 2" className="akebono-sp-collage-img akebono-sp-img-right" />
                                <img src="/images/shodo-3.jpg" alt="Calligraphy 3" className="akebono-sp-collage-img akebono-sp-img-left" />
                            </div>
                        </div>
                    </motion.div>

                    {/* ====== 4. 金箔体験 (1枚画像・モーダル詳細表示) ====== */}
                    <motion.div id="kinpaku-section" className="akebono-sp-section scroll-target" {...fadeInProps}>
                        <h2 className="akebono-sp-title">
                            <span className="akebono-sp-num">04</span>
                            {content.kinpaku.title}
                        </h2>
                        <div className="akebono-sp-layout">
                            <div className="akebono-sp-img-wrapper" style={{ maxHeight: '450px', alignSelf: 'center' }}>
                                <img src="/images/kinpaku-main.jpg" alt="Imai Kinpaku" />
                            </div>
                            <div className="akebono-sp-content-box">
                                <h3 className="akebono-sp-instructor-name">{content.kinpaku.instructorName}</h3>
                                <p style={{ marginBottom: '15px' }}>{content.kinpaku.desc}</p>

                                <div className="akebono-sp-feature-box">
                                    <p><strong>{isEnglish ? 'Exclusive Perk:' : 'あけぼの限定特典：'}</strong></p>
                                    <p>{content.kinpaku.displayFeature}</p>
                                </div>

                                <ul className="akebono-sp-info-list">
                                    <li><strong>Price:</strong> {content.kinpaku.price}</li>
                                    <li><strong>Duration:</strong> {content.kinpaku.duration}</li>
                                    <li><strong>Location:</strong> {content.kinpaku.location}</li>
                                    <li><strong>Access:</strong> {content.kinpaku.access}</li>
                                </ul>

                                <button onClick={() => setIsKinpakuModalOpen(true)} className="akebono-sp-btn-gold">
                                    {content.kinpaku.btnItinerary}
                                </button>
                                <br />
                                <a href={content.kinpaku.linkUrl} target="_blank" rel="noopener noreferrer" className="akebono-sp-link">
                                    {content.kinpaku.linkText}
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* ====== お申し込みフォーム ====== */}
                    <motion.div className="akebono-sp-form-section" {...fadeInProps}>
                        <h2>{content.formTitle}</h2>
                        <form ref={form} onSubmit={sendEmail}>
                            <div className="akebono-sp-form-group akebono-sp-highlight">
                                <label>{content.formExpLabel} <span className="akebono-sp-req">*</span></label>
                                <select name="experience" required value={selectedExp} onChange={(e) => setSelectedExp(e.target.value)}>
                                    <option value="tea">{content.formExpOptionTea}</option>
                                    <option value="yuzen">{content.formExpOptionYuzen}</option>
                                    <option value="shodo">{content.formExpOptionShodo}</option>
                                    <option value="kinpaku">{content.formExpOptionKinpaku}</option>
                                    <option value="multiple">{content.formExpOptionMultiple}</option>
                                </select>
                            </div>

                            {/* 加賀友禅専用オプション */}
                            {(selectedExp === 'yuzen' || selectedExp === 'multiple') && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="akebono-sp-form-group">
                                    <label>{content.formPlanLabel} <span className="akebono-sp-req">*</span></label>
                                    <select name="guide_plan" required className="akebono-sp-sub-select">
                                        <option value={content.formPlanOption1}>{content.formPlanOption1}</option>
                                        <option value={content.formPlanOption2}>{content.formPlanOption2}</option>
                                    </select>
                                </motion.div>
                            )}

                            <div className="akebono-sp-form-group">
                                <label>{content.formName} <span className="akebono-sp-req">*</span></label>
                                <input type="text" name="user_name" required placeholder={isEnglish ? "Full Name" : "山田 太郎"} />
                            </div>
                            <div className="akebono-sp-form-group">
                                <label>{content.formEmail} <span className="akebono-sp-req">*</span></label>
                                <input type="email" name="user_email" required placeholder="email@example.com" />
                            </div>
                            <div className="akebono-sp-form-group">
                                <label>{content.formDate} <span className="akebono-sp-req">*</span></label>
                                <input type="text" name="desired_date" required placeholder={isEnglish ? "Desired Date" : "例: 2026年10月15日 10:00"} />
                            </div>
                            <div className="akebono-sp-form-group">
                                <label>{content.formParticipants} <span className="akebono-sp-req">*</span></label>
                                <input type="number" name="participants" min="1" max="10" required />
                            </div>
                            <div className="akebono-sp-form-area">
                                <label>{content.formMessage}</label>
                                <textarea name="message" rows="4"></textarea>
                            </div>
                            <div className="akebono-sp-recaptcha">
                                <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} hl={isEnglish ? 'en' : 'ja'} />
                            </div>
                            <button type="submit" disabled={isSubmitting} className="akebono-sp-submit">
                                {isSubmitting ? '...' : content.submitButton}
                            </button>
                        </form>
                        {submitMessage && (
                            <div className={`akebono-sp-msg-box ${submitMessage.includes('Failed') || submitMessage.includes('失敗') ? 'error' : 'success'}`}>
                                {submitMessage}
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* ポップアップ：加賀友禅 */}
                {isYuzenModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsYuzenModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsYuzenModalOpen(false)}>×</button>
                            <h3 className="akebono-sp-modal-title">{content.yuzen.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.yuzen.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.yuzen.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ポップアップ：書道 */}
                {isShodoModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsShodoModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsShodoModalOpen(false)}>×</button>
                            <h3 className="akebono-sp-modal-title">{content.shodo.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.shodo.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.shodo.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ポップアップ：金箔 */}
                {isKinpakuModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsKinpakuModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsKinpakuModalOpen(false)}>×</button>
                            <h3 className="akebono-sp-modal-title">{content.kinpaku.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.kinpaku.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.kinpaku.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default InternalTestExperiencesPage;